import React, { useEffect, useState } from 'react'
import {
  Button,
  Table,
  message,
  Select,
  Empty,
  Input,
  Drawer,
  Form,
  Popconfirm,
  DatePicker,
} from 'antd'
import TextArea from 'antd/lib/input/TextArea'
import { EditableField } from '../common'
import { Loading } from '@guillotinaweb/react-gmi'
import { endOfYesterday, startOfDay } from 'date-fns'
import { toISOStringWithTimezone } from '../../../utils'
import { useLocation } from 'wouter'
import { ContentLayout, Error } from '../../../common/content-layout'
import { useMutation, useQuery } from 'urql'
import {
  CancelOrdersMutation,
  CommentOrdersMutation,
  ConfirmOrdersMutation,
  CreateAddressMutation,
  HibernateMutation,
  RemoveOrderMutation,
  SelectBillingAddressMutation,
  SetInvoicedMutation,
  ViewInvoiceQuery,
  ViewUserQuery,
} from './graphql'
import CheckPaymentMultiOrder from '../../../components/check_payment_multiorder'

import './styles.css'

export function ViewInvoice({ id }) {
  const [selectedRows, setSelectedRows] = useState([])
  const [, setLocation] = useLocation()
  const [openCreate, setOpenCreate] = useState(false)
  const [hibernateDate, setHibernateDate] = useState(null)
  const [selecting, setSelecting] = useState(false)
  const [observationType, setObservationType] = useState('GENERAL')
  const [comment, setComment] = useState('')
  const [, hibernate] = useMutation(HibernateMutation)
  const [, commentOrder] = useMutation(CommentOrdersMutation)
  const [, confirmOrder] = useMutation(ConfirmOrdersMutation)
  const [, cancelOrder] = useMutation(CancelOrdersMutation)
  const [, selectBillingAddress] = useMutation(SelectBillingAddressMutation)
  const [, setInvoiced] = useMutation(SetInvoicedMutation)
  const [, removeOrders] = useMutation(RemoveOrderMutation)
  const [resInvoice, reset] = useQuery({
    query: ViewInvoiceQuery,
    variables: { id },
    ...(window.location.search.includes('refresh=1')
      ? {
          requestPolicy: 'network-only', // invalidate cache
        }
      : {}),
  })
  const invoice = resInvoice.data?.invoice_multiorder || {}
  const [resUser, resetUser] = useQuery({
    query: ViewUserQuery,
    variables: { id: invoice.id_usuario },
    pause: invoice.id_usuario === undefined,
  })
  const editable = invoice.status === 'DRAFT'
  const customer = resUser.data?.user || {}

  function getTableAction({ verb, text, content = null, onAction }) {
    return {
      key: verb,
      disabled: !editable,
      text: (
        <Popconfirm
          placement="topLeft"
          title={
            <>
              <div>Are you sure you want to {verb} the selected orders?</div>
              {content}
            </>
          }
          onConfirm={onAction}
          okText={`Yes, ${verb}`}
          disabled={!selectedRows?.length}
          cancelText="No"
        >
          {text}
        </Popconfirm>
      ),
      onSelect: () =>
        !selectedRows?.length &&
        message.error(`You must select which orders you want to ${verb}`),
    }
  }

  async function action({ promise, dataName, verb }) {
    try {
      const res = await promise
      const error = res.error?.message || res.data?.[dataName]?.errors
      if (error) return message.error(JSON.stringify(error))
      message.success(`Selected orders have been successfully ${verb}`)
      reset({ requestPolicy: 'network-only' })
    } catch (e) {
      message.error(e.message)
    }
  }

  async function onSetInvoiced() {
    if (!editable) {
      window.open(
        `/administracion/facturas/detalle_factura.html?id_factura=${invoice.id_factura}`,
        '_blank'
      )
      return
    }

    try {
      const res = await setInvoiced({ id })
      if (res.error) return message.error(res.error.message)
      if (res.data.invoice_multiorder_set_invoiced?.status === 'KO') {
        return message.error('The invoice could not be marked as invoiced')
      }
      message.success(`#${id} invoiced!`)
      setLocation('/orders/multi-order-invoice?refresh=1')
    } catch (e) {
      message.error(e.message)
    }
  }

  async function selectAddress(id_fac) {
    setSelecting(true)
    const res = await selectBillingAddress({ id, id_fac })
    if (res.error) return message.error(res.error.message)
    message.success('The selected billing address has been saved correctly')
    reset({ requestPolicy: 'network-only' })
  }

  useEffect(() => {
    if (!resInvoice.fetching) setSelecting(false)
  }, [resInvoice.fetching])

  return (
    <ContentLayout
      displayBackBtn
      title={`Multi-order invoice #${id}`}
      buttonLabel={editable ? 'Create invoice' : 'View invoice'}
      loading={resInvoice.fetching && !selecting}
      error={resInvoice.error}
      buttonAction={onSetInvoiced}
    >
      <EditableField label="ID" defaultValue={id} editable={false} />
      {resUser.fetching && <Loading />}
      {resUser.error && <Error message={resUser.error.message} />}
      {!resUser.fetching && !resUser.error && (
        <>
          <EditableField
            className="customer-email"
            label="Customer"
            defaultValue={customer.email}
            editable={false}
          />
          <EditableField
            label="Billing address"
            editable={false}
            children={() => (
              <>
                <Select
                  showSearch
                  disabled={!editable}
                  style={{ width: 300 }}
                  key={invoice.id_datos_fac}
                  defaultValue={invoice.id_datos_fac}
                  placeholder="Select a billing address"
                  optionFilterProp="children"
                  onChange={selectAddress}
                  filterOption={(input, option) => {
                    const text =
                      option?.children?.props?.children
                        ?.map((c) => c?.props?.children)
                        ?.join(' ') || ''
                    return text.toLowerCase().indexOf(input.toLowerCase()) >= 0
                  }}
                >
                  {(customer.datos_fac || []).map((d) => (
                    <Select.Option key={d.id_datos_fac} value={d.id_datos_fac}>
                      <div>
                        {d.nombre && <div>{d.nombre}</div>}
                        {d.linea1 && <div>{d.linea1}</div>}
                        {d.linea2 && <div>{d.linea2}</div>}
                        {d.linea3 && <div>{d.linea3}</div>}
                      </div>
                    </Select.Option>
                  ))}
                  {!customer.datos_fac?.length && <Empty />}
                </Select>
                <Button
                  disabled={!editable}
                  onClick={() => setOpenCreate(true)}
                  type="default"
                >
                  Add new one
                </Button>
              </>
            )}
          />
          {selecting && <Loading />}
          {invoice.id_factura ? (
            <CheckPaymentMultiOrder
              {...{
                id_multifactura: id,
                id_factura: invoice.id_factura,
              }}
            />
          ) : null}
        </>
      )}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          marginTop: 40,
        }}
      >
        <h2 className="title is-size-5">Orders</h2>
        <Button
          type="primary"
          disabled={!editable || selecting}
          onClick={() => {
            if (!invoice.id_datos_fac)
              return message.error('You must first select a billing address')
            setLocation(`/orders/multi-order-invoice/${id}/new`)
          }}
        >
          Add
        </Button>
      </div>
      <Table
        pagination={{ pageSize: 100 }}
        rowSelection={{
          selectedRows,
          onChange: setSelectedRows,
          selections: [
            getTableAction({
              verb: 'remove',
              text: 'Remove',
              onAction: () =>
                action({
                  promise: removeOrders({ id, orderIds: selectedRows }),
                  dataName: 'invoice_multiorder_update',
                  verb: 'removed',
                }),
            }),
            getTableAction({
              verb: 'hibernate',
              text: 'Hibernate',
              content: (
                <div style={{ margin: '20px 0' }}>
                  Enter the date:{' '}
                  <DatePicker
                    disabledDate={(c) => c && c < endOfYesterday()}
                    onChange={(e) => {
                      if (!e) return setHibernateDate(null)
                      const date = toISOStringWithTimezone(
                        startOfDay(e.toDate())
                      )
                      setHibernateDate(date)
                    }}
                  />
                </div>
              ),
              onAction: () => {
                if (!hibernateDate)
                  return message.error('You must enter the hibernation date')
                action({
                  promise: hibernate({
                    date: new Date(hibernateDate),
                    orderIds: selectedRows,
                  }),
                  dataName: 'batch_pedido_hibernate',
                  verb: 'hibernated',
                })
              },
            }),
            getTableAction({
              verb: 'confirm',
              text: 'Confirm payment',
              onAction: () =>
                action({
                  promise: confirmOrder({ orderIds: selectedRows }),
                  dataName: 'batch_pedido_confirm_payment',
                  verb: 'confirmed',
                }),
            }),
            getTableAction({
              verb: 'cancel',
              text: 'Cancel',
              onAction: () =>
                action({
                  promise: cancelOrder({ orderIds: selectedRows }),
                  dataName: 'batch_pedido_cancel',
                  verb: 'canceled',
                }),
            }),
            getTableAction({
              verb: 'comment',
              text: 'Add comment',
              content: (
                <div style={{ margin: '20px 0' }}>
                  <div>Type of observation:</div>
                  <Select
                    defaultValue={observationType}
                    style={{ width: '100%' }}
                    onChange={setObservationType}
                  >
                    <Select.Option key="general" value="GENERAL">
                      General
                    </Select.Option>
                    <Select.Option key="preparacion" value="PREPARACION">
                      Preparation
                    </Select.Option>
                    <Select.Option key="entrega" value="ENTREGA">
                      Delivery
                    </Select.Option>
                  </Select>
                  <div style={{ marginTop: 20 }}>Comment:</div>
                  <TextArea
                    onChange={(e) => setComment(e.target.value)}
                    placeholder="Write here the comment"
                  />
                </div>
              ),
              onAction: () =>
                action({
                  promise: commentOrder({
                    orderIds: selectedRows,
                    type: observationType,
                    comment,
                  }),
                  dataName: 'batch_pedido_addcomment',
                  verb: 'commented',
                }),
            }),
          ],
          getCheckboxProps: (record) => ({
            disabled: !editable,
            name: record.name,
          }),
        }}
        dataSource={invoice.pedidos?.slice()?.reverse()}
        columns={[
          {
            title: 'Cod pedido',
            dataIndex: 'cod_pedido',
            key: 'cod_pedido',
            render: (v, r) => (
              <a
                href={`/administracion/pedidos/detalle_pedido.html?id_pedido=${r.id_pedido}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                {v}
              </a>
            ),
          },
          {
            title: 'Send address',
            key: 'foto_direccion',
            dataIndex: 'foto_direccion',
            render: (v) => (
              <>
                <div>{v.linea1}</div>
                <div>{v.linea2}</div>
                <div>{v.linea3}</div>
              </>
            ),
          },
          {
            title: 'Products',
            dataIndex: 'lines',
            key: 'lines',
            render: (v, r) =>
              v.map((line) => (
                <div key={line.nombre + r.id_pedido}>
                  {line.nombre} <b>x{line.cantidad}</b>
                </div>
              )),
          },
          {
            title: 'Status',
            dataIndex: 'id_estado_pd',
            key: 'id_estado_pd',
          },
        ]}
        rowKey={(record) => record.id_pedido}
        size="small"
      />
      <Drawer
        title="Add a new billing address"
        width={500}
        onClose={() => setOpenCreate(false)}
        visible={openCreate}
        bodyStyle={{ paddingBottom: 80 }}
      >
        <CreateAddress
          userId={parseInt(invoice.id_usuario, 10)}
          key={openCreate}
          onSuccess={async (data) => {
            await selectAddress(data.user_add_datos_fac.id_datos_fac)
            await setOpenCreate(false)
            resetUser({ requestPolicy: 'network-only' })
          }}
        />
      </Drawer>
    </ContentLayout>
  )
}

function CreateAddress({ onSuccess, userId }) {
  const [loading, setLoading] = useState(false)
  const [, create] = useMutation(CreateAddressMutation)

  return (
    <Form
      name="basic"
      className="multiorder create-billing-address"
      onFinish={async (fac) => {
        try {
          setLoading(true)
          const res = await create({ userId, ...fac })
          if (res.error?.message) return message.error(res.error?.message)
          message.success('Created!')
          onSuccess(res.data)
        } catch (e) {
          message.error(e.message)
        } finally {
          setLoading(false)
        }
      }}
    >
      <Form.Item
        label="Name"
        name="nombre"
        rules={[{ required: true, message: 'Please input the name' }]}
      >
        <Input />
      </Form.Item>
      <Form.Item
        label="CIF"
        name="doc_id"
        rules={[{ required: true, message: 'Please input the CIF' }]}
      >
        <Input />
      </Form.Item>
      <Form.Item
        label="Line 1"
        name="linea1"
        rules={[{ required: true, message: 'Please input the line 1' }]}
      >
        <Input />
      </Form.Item>
      <Form.Item label="Line 2" name="linea2">
        <Input />
      </Form.Item>
      <Form.Item label="Line 3" name="linea3">
        <Input />
      </Form.Item>
      <Form.Item
        label="Postal code"
        name="codigo_postal"
        rules={[{ required: true, message: 'Please input the postal code' }]}
      >
        <Input />
      </Form.Item>
      <Form.Item
        label="City"
        name="ciudad"
        rules={[{ required: true, message: 'Please input the city' }]}
      >
        <Input />
      </Form.Item>
      <Form.Item
        label="Phone"
        name="telefono"
        rules={[{ required: true, message: 'Please input the phone' }]}
      >
        <Input />
      </Form.Item>
      <Form.Item
        label="Country id"
        name="id_pais"
        rules={[
          { required: true, message: 'Please input the country id' },
          { max: 2, message: 'The country code is only 2 characters long' },
        ]}
      >
        <Input />
      </Form.Item>
      <Form.Item>
        <Button disabled={loading} type="primary" htmlType="submit">
          Create
        </Button>
      </Form.Item>
      {loading && <Loading />}
    </Form>
  )
}
